/**
 * IncidentReportView - Full-screen incident reporting
 * Follows pattern from MaintenanceCheckView and DefectRecordView
 * Includes WBC3 incident reporting requirements
 */

import React, { useState, useEffect } from 'react';
import { AlertTriangle, Save } from 'lucide-react';
import { useCrew } from '../../../../hooks/useCrew';
import { useCustomDialogs } from '../../../../hooks/useCustomDialogs';
// FT-356c: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header raw ArrowLeft back button removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import { 
  BasicInfoSection,
  SeverityImpactSection
} from './components/IncidentFormSections';
import EnvironmentalConditionsSection from './components/EnvironmentalConditionsSection';
import PeopleInvolvedSection from './components/PeopleInvolvedSection';
import NextStepsInfo from './components/NextStepsInfo';
import IncidentReportingGuidance from './components/IncidentReportingGuidance';

const IncidentReportView = ({ onBack, onSave, initialData = null, editReason = '' }) => {
  const { crew } = useCrew();
  const { showAlert, AlertDialog } = useCustomDialogs();
  const isEditing = !!initialData;

  // FT-356c: register parent's onBack as the shell-level back handler.
  useSetBackHandler(onBack);

  const [formData, setFormData] = useState({
    // Basic incident info
    type: 'accident',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    location: '',
    locationOther: '',
    
    // Environmental conditions (WBC3 requirement)
    weatherConditions: '',
    visibility: 'good',
    daylight: 'daylight',
    seaState: '',
    
    // Incident details
    description: '',
    immediateActions: '',
    
    // People involved
    reportedBy: '',
    personsInvolved: [],
    witnesses: [],
    
    // Severity indicators
    injuries: false,
    injuryDetails: '',
    damage: false,
    damageDetails: '',
    pollutionRisk: false,
    
    // Severity indicators for reporting requirements
    vesselLost: false,
    vesselAbandoned: false,
    grounding: false,
    collision: false,
    fire: false,
    flooding: false,
    navigationSafetyCompromised: false,
    emergencyServicesContacted: false,
    
    // Medical response indicators (may trigger external reporting)
    medicalEvacuation: false,
    hospitalization: false,
    ambulanceCalled: false,
    coastguardMedicalAdvice: false,
    
    // Initial observations only - not root cause analysis
    observedConditions: '',
    additionalInfo: '',
    windForce: ''
  });

  const [errors, setErrors] = useState({});
  const [showDetailedGuidance, setShowDetailedGuidance] = useState(false);

  // Initialize form when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        type: initialData.type || 'accident',
        date: initialData.date || new Date().toISOString().split('T')[0],
        time: initialData.time || new Date().toTimeString().slice(0, 5),
        location: initialData.location || '',
        locationOther: initialData.locationOther || '',
        weatherConditions: initialData.weatherConditions || '',
        visibility: initialData.visibility || 'good',
        daylight: initialData.daylight || 'daylight',
        seaState: initialData.seaState || '',
        description: initialData.description || '',
        immediateActions: initialData.immediateActions || '',
        reportedBy: initialData.reportedBy || '',
        personsInvolved: initialData.personsInvolved || [],
        witnesses: initialData.witnesses || [],
        injuries: initialData.injuries || false,
        injuryDetails: initialData.injuryDetails || '',
        damage: initialData.damage || false,
        damageDetails: initialData.damageDetails || '',
        pollutionRisk: initialData.pollutionRisk || false,
        vesselLost: initialData.vesselLost || false,
        vesselAbandoned: initialData.vesselAbandoned || false,
        grounding: initialData.grounding || false,
        collision: initialData.collision || false,
        fire: initialData.fire || false,
        flooding: initialData.flooding || false,
        navigationSafetyCompromised: initialData.navigationSafetyCompromised || false,
        emergencyServicesContacted: initialData.emergencyServicesContacted || false,
        medicalEvacuation: initialData.medicalEvacuation || false,
        hospitalization: initialData.hospitalization || false,
        ambulanceCalled: initialData.ambulanceCalled || false,
        coastguardMedicalAdvice: initialData.coastguardMedicalAdvice || false,
        observedConditions: initialData.observedConditions || '',
        additionalInfo: initialData.additionalInfo || '',
        windForce: initialData.windForce || ''
      });
    }
  }, [initialData]);

  const incidentTypes = [
    { value: 'accident', label: 'Accident' },
    { value: 'near_miss', label: 'Near Miss' },
    { value: 'hazard', label: 'Hazard/Unsafe Condition' },
    { value: 'environmental', label: 'Environmental' },
    { value: 'equipment_failure', label: 'Equipment Failure' },
    { value: 'personnel_injury', label: 'Personnel Injury' }
  ];

  const locations = [
    { value: 'deck', label: 'Deck' },
    { value: 'engine_room', label: 'Engine Room' },
    { value: 'bridge', label: 'Bridge' },
    { value: 'galley', label: 'Galley' },
    { value: 'accommodation', label: 'Accommodation' },
    { value: 'cargo_area', label: 'Cargo Area' },
    { value: 'machinery_space', label: 'Machinery Space' },
    { value: 'steering_gear', label: 'Steering Gear Compartment' },
    { value: 'other', label: 'Other' }
  ];


  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handlePersonToggle = (crewId, field = 'personsInvolved') => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(crewId)
        ? prev[field].filter(id => id !== crewId)
        : [...prev[field], crewId]
    }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 50) {
      newErrors.description = 'Description must be at least 50 characters. Please provide more detail about what happened.';
    }
    
    if (!formData.location) {
      newErrors.location = 'Location is required';
    }
    
    if (formData.location === 'other' && !formData.locationOther.trim()) {
      newErrors.locationOther = 'Please specify location';
    }
    
    if (!formData.reportedBy) {
      newErrors.reportedBy = 'Reporter is required';
    }
    
    if (formData.injuries && !formData.injuryDetails.trim()) {
      newErrors.injuryDetails = 'Please describe injuries';
    }
    
    if (formData.damage && !formData.damageDetails.trim()) {
      newErrors.damageDetails = 'Please describe damage';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      showAlert('Please fill in all required fields', '', 'error');
      return;
    }

    // Prepare final data
    const finalLocation = formData.location === 'other'
      ? formData.locationOther
      : locations.find(l => l.value === formData.location)?.label || formData.location;

    const incidentRecord = {
      ...formData,
      location: finalLocation,
      id: initialData?.id, // Keep existing ID when editing
      status: initialData?.status || 'reported',
      reportedAt: initialData?.reportedAt || new Date().toISOString(),
      createdAt: initialData?.createdAt || new Date().toISOString(),
      updatedAt: isEditing ? new Date().toISOString() : undefined,
      // Preserve audit log and other fields from initialData
      auditLog: initialData?.auditLog || [],
      vesselId: initialData?.vesselId,
      investigation: initialData?.investigation,
      correctiveActions: initialData?.correctiveActions
    };

    // Pass to parent save handler
    onSave(incidentRecord);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* FT-356c: header raw ArrowLeft back button removed — bottom bar owns Back. */}
            <div className="flex items-center gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                  <h1 className="text-xl font-semibold text-gray-900">
                    {isEditing ? 'Edit Incident' : 'Report Incident'}
                  </h1>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {isEditing
                    ? 'Update incident details for SMS compliance'
                    : 'Document vessel incidents for SMS compliance and investigation'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-5xl mx-auto px-6 py-6 pb-24 lg:pb-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Basic Information */}
          <BasicInfoSection
            formData={formData}
            handleInputChange={handleInputChange}
            incidentTypes={incidentTypes}
            locations={locations}
            errors={errors}
          />

          {/* Environmental Conditions */}
          <EnvironmentalConditionsSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />

          {/* Incident Description */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Incident Description
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  What Happened? <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={6}
                  placeholder="Provide a detailed, factual description including:
• Exact sequence of events leading to the incident
• What the person(s) involved were doing at the time
• Location and environmental conditions
• Any equipment, tools, or materials involved
• Initial consequences or injuries
Example: 'While securing mooring lines on the fore deck during berthing operations, AB Smith stepped over a temporary air hose that had been laid across the deck for pneumatic tools. The hose was not properly marked with hazard tape. As he stepped over, his foot caught on the hose causing him to trip and fall forward onto the deck, landing on his left knee and hands...'"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Minimum 50 characters required. Be specific - this information is critical for investigation and prevention.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Immediate Actions Taken
                </label>
                <textarea
                  value={formData.immediateActions}
                  onChange={(e) => handleInputChange('immediateActions', e.target.value)}
                  rows={3}
                  placeholder="What was done immediately after the incident..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observed Conditions
                </label>
                <textarea
                  value={formData.observedConditions}
                  onChange={(e) => handleInputChange('observedConditions', e.target.value)}
                  rows={2}
                  placeholder="Describe any relevant conditions observed at the time..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Information
                </label>
                <textarea
                  value={formData.additionalInfo}
                  onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                  rows={2}
                  placeholder="Any other relevant factual information..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            </div>
          </div>

          {/* People Involved */}
          <PeopleInvolvedSection 
            formData={formData}
            handleInputChange={handleInputChange}
            handlePersonToggle={handlePersonToggle}
            crew={crew}
            errors={errors}
          />

          {/* Severity & Impact */}
          <SeverityImpactSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />

          {/* Reporting Guidance - Shows requirements based on severity indicators */}
          <IncidentReportingGuidance 
            incidentData={formData} 
            isWizardMode={false}
            onViewDetails={() => setShowDetailedGuidance(true)}
          />

          {/* Info Box */}
          <NextStepsInfo />

          {/* Submit Buttons - Inline with form */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
            >
              <Save className="inline-block h-4 w-4 mr-2" />
              {isEditing ? 'Save Changes' : 'Submit Report'}
            </button>
          </div>

        </form>
      </div>

      {/* Detailed Reporting Guidance Modal */}
      {showDetailedGuidance && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setShowDetailedGuidance(false)} />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  Detailed Reporting Requirements
                </h2>
                <button
                  onClick={() => setShowDetailedGuidance(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="max-h-[calc(90vh-8rem)] overflow-y-auto p-6">
                <IncidentReportingGuidance 
                  incidentData={formData} 
                  isWizardMode={true}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertDialog />
    </div>
  );
};

export default IncidentReportView;